import { type ReactElement } from 'react';
import { type VehicleCycledEnergyRecord } from '../services/battery-monitoring-api/types/battery-monitoring-api';
import { type UpdateQueryParameterValues } from '../hooks/use-update-query-parameter-values';
import styles from './style.module.scss';
import CountCard from './CountCard';
import PropulsionCountCard from './PropulsionCountCard';
import StateOfHealthChart from './StateOfHealthChart';
import CycledPerKilometerChart from './CycledPerKilometerChart';
import useBusCount from './hooks/use-bus-count';
import useTruckCount from './hooks/use-truck-count';
import useAlertCount from './hooks/use-alert-count';
import useCustomerCount from './hooks/use-customer-count';
import { type SelectedChartSegment } from '@/app/config/chart-segments';

interface OverviewProperties {
  readonly stateOfHealthChartVehicles: VehicleCycledEnergyRecord[];
  readonly cycledPerKilometerChartVehicles: VehicleCycledEnergyRecord[];
  readonly sortedVehicles: VehicleCycledEnergyRecord[];
  readonly selectedStateOfHealthSegment: SelectedChartSegment | null;
  readonly selectedCycledEnergySegment: SelectedChartSegment | null;
  readonly updateQueryParameterValues: UpdateQueryParameterValues;
}

const Overview = ({
  stateOfHealthChartVehicles,
  cycledPerKilometerChartVehicles,
  sortedVehicles,
  selectedStateOfHealthSegment,
  selectedCycledEnergySegment,
  updateQueryParameterValues,
}: OverviewProperties): ReactElement => {
  const customerCount = useCustomerCount(sortedVehicles);
  const busCount = useBusCount(sortedVehicles);
  const truckCount = useTruckCount(sortedVehicles);
  const phevBusCount = useBusCount(sortedVehicles, 'PHEV');
  const phevTruckCount = useTruckCount(sortedVehicles, 'PHEV');
  const alertCount = useAlertCount(sortedVehicles);

  return (
    <div className={styles.container}>
      <div className={styles.top}>
        <CountCard header="Selected Customers" iconName="profile" count={customerCount} />
        <PropulsionCountCard vehicleType="Bus" bevCount={busCount} phevCount={phevBusCount} />
        <PropulsionCountCard vehicleType="Truck" bevCount={truckCount} phevCount={phevTruckCount} />
        <CountCard header="Alerts" iconName="warning" iconColorClassName="tds-text-warning" count={alertCount} />
      </div>
      <div className={styles.bottom}>
        <StateOfHealthChart
          chartVehicles={stateOfHealthChartVehicles}
          selectedStateOfHealthSegment={selectedStateOfHealthSegment}
          updateQueryParameterValues={updateQueryParameterValues}
        />
        <CycledPerKilometerChart
          chartVehicles={cycledPerKilometerChartVehicles}
          selectedCycledEnergySegment={selectedCycledEnergySegment}
          updateQueryParameterValues={updateQueryParameterValues}
        />
      </div>
    </div>
  );
};

export default Overview;
